import {
  ClockRewind,
  Edit04,
  MessageChatCircle,
  Stars01,
} from '@untitledui/icons';
import React, { useMemo } from 'react';

import {
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { useOrgTimeFormat } from '@/lib/time';

import type { ProposedAction } from '../../lib/proposals/core/types';

interface VersionHistorySidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  versions: ProposedAction[];
  onVersionClick?: (version: ProposedAction) => void;
  currentVersionId?: number;
}

// Group type for categorizing versions
type VersionGroup = {
  type: 'ai' | 'manual' | 'chat';
  label: string;
  versions: ProposedAction[];
};

// Helper: Get version type (outside component to avoid recreation)
function getVersionType(version: ProposedAction): 'ai' | 'manual' | 'chat' {
  if (version.version_notes) {
    if (version.version_notes.includes('Auto-versioned')) {
      return 'manual';
    }
    if (version.version_notes.toLowerCase().includes('chat')) {
      return 'chat';
    }
  }
  if (version.created_by_agent) {
    return 'ai';
  }
  return 'manual';
}

// Helper: Get group label (outside component to avoid recreation)
function getGroupLabel(type: 'ai' | 'manual' | 'chat', count: number): string {
  switch (type) {
    case 'ai':
      return count === 1 ? 'AI edit' : `${count} AI edits`;
    case 'manual':
      return count === 1 ? 'Manual edit' : `${count} manual edits`;
    case 'chat':
      return count === 1 ? 'Chat edit' : `${count} chat edits`;
  }
}

// "Jul 15, 14:30"-style label options for the org-bound formatter. The
// formatter itself comes from useOrgTimeFormat() so the time portion picks up
// the org format and the midnight-safe hour cycle.
const VERSION_TIMESTAMP_OPTIONS = {
  locale: 'en-US',
  dateParts: { month: 'short', day: 'numeric' },
} as const;

function getVersionIcon(type: 'ai' | 'manual' | 'chat'): React.ReactElement {
  const iconClasses = 'w-4 h-4';
  switch (type) {
    case 'ai':
      return <Stars01 className={iconClasses} />;
    case 'chat':
      return <MessageChatCircle className={iconClasses} />;
    default:
      return <Edit04 className={iconClasses} />;
  }
}

export default function VersionHistorySidebar({
  isOpen,
  onOpenChange,
  versions,
  onVersionClick,
  currentVersionId,
}: VersionHistorySidebarProps) {
  // Group consecutive versions by type
  const versionGroups = useMemo<VersionGroup[]>(() => {
    if (!versions.length) {
      return [];
    }

    const groups: VersionGroup[] = [];
    let currentGroup: ProposedAction[] = [];
    let currentType: 'ai' | 'manual' | 'chat' | null = null;

    // Sort by version number descending (newest first)
    const sortedVersions = [...versions].sort(
      (a, b) => (b.version_number || 0) - (a.version_number || 0)
    );

    sortedVersions.forEach(version => {
      const type = getVersionType(version);

      if (type !== currentType) {
        // Save previous group
        if (currentGroup.length > 0 && currentType) {
          groups.push({
            type: currentType,
            label: getGroupLabel(currentType, currentGroup.length),
            versions: currentGroup,
          });
        }
        // Start new group
        currentGroup = [version];
        currentType = type;
      } else {
        currentGroup.push(version);
      }
    });

    // Save last group
    if (currentGroup.length > 0 && currentType) {
      groups.push({
        type: currentType,
        label: getGroupLabel(currentType, currentGroup.length),
        versions: currentGroup,
      });
    }

    return groups;
  }, [versions]);

  const headerContent = useMemo(
    () => (
      <div className='flex items-center gap-2'>
        <ClockRewind className='w-5 h-5 text-tertiary' />
        <h2 className='text-lg font-semibold text-primary'>Version history</h2>
      </div>
    ),
    []
  );

  return (
    <SlideoutMenu
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      resizable
      header={headerContent}
      dialogAriaLabel='Version history'
    >
      {versions.length === 0 ? (
        <p className='text-sm text-tertiary text-center py-8'>
          No version history
        </p>
      ) : (
        <div className='space-y-1 py-4'>
          {versionGroups.map(group => (
            <VersionGroupItem
              key={`${group.type}-${group.versions[0]?.id ?? group.versions[0]?.version_number ?? 0}`}
              group={group}
              onVersionClick={onVersionClick}
              currentVersionId={currentVersionId}
            />
          ))}
        </div>
      )}
    </SlideoutMenu>
  );
}

// Collapsible group component
function VersionGroupItem({
  group,
  onVersionClick,
  currentVersionId,
}: {
  group: VersionGroup;
  onVersionClick?: (version: ProposedAction) => void;
  currentVersionId?: number;
}) {
  // Org default read from context — not threaded through props.
  const { formatTime } = useOrgTimeFormat();
  // Single version - show directly without collapse
  if (group.versions.length === 1) {
    const version = group.versions[0];
    const isCurrent = version.id === currentVersionId;

    return (
      <button
        onClick={() => onVersionClick?.(version)}
        aria-current={isCurrent ? 'true' : undefined}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors cursor-pointer ${
          isCurrent
            ? 'bg-secondary ring-1 ring-[var(--color-border-secondary)] ring-inset'
            : 'hover:bg-secondary'
        }`}
      >
        <div
          className={`flex-shrink-0 ${isCurrent ? 'text-primary' : 'text-tertiary'}`}
        >
          {getVersionIcon(group.type)}
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-semibold text-primary'>
              v{version.version_number}
            </span>
            {isCurrent && (
              <span className='text-xs px-1.5 py-0.5 bg-tertiary text-secondary rounded font-medium'>
                Current
              </span>
            )}
          </div>
          <div className='flex items-center gap-2 text-xs text-tertiary mt-0.5'>
            <span>
              {(
                {
                  ai: 'AI edit',
                  chat: 'Chat edit',
                  manual: 'Manual edit',
                } as const
              )[group.type] ?? 'Manual edit'}
            </span>
            <span>·</span>
            <span>
              {version.created_at
                ? formatTime(version.created_at, VERSION_TIMESTAMP_OPTIONS)
                : ''}
            </span>
          </div>
        </div>
      </button>
    );
  }

  // Multiple versions - collapsible group
  return (
    <CollapsibleVersionGroup
      group={group}
      onVersionClick={onVersionClick}
      currentVersionId={currentVersionId}
    />
  );
}

// Collapsible group for multiple consecutive versions of same type
function CollapsibleVersionGroup({
  group,
  onVersionClick,
  currentVersionId,
}: {
  group: VersionGroup;
  onVersionClick?: (version: ProposedAction) => void;
  currentVersionId?: number;
}) {
  // Org default read from context — not threaded through props.
  const { formatTime } = useOrgTimeFormat();
  const firstVersion = group.versions[0];
  const lastVersion = group.versions[group.versions.length - 1];
  const containsCurrent = group.versions.some(v => v.id === currentVersionId);

  return (
    // Migrated from native <details>/<summary> to the shared
    // <AccordionItem>: same visual / interaction model, but inherits the
    // smooth grid-rows expand animation and react-aria a11y wiring.
    // Group header is a real <button> via <AccordionTrigger> — child
    // version <button>s sit OUTSIDE in <AccordionPanel> so we don't
    // nest interactive elements.
    <AccordionItem>
      <AccordionTrigger
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left ${
          containsCurrent ? 'bg-secondary' : 'hover:bg-secondary'
        }`}
      >
        <div
          className={`flex-shrink-0 ${containsCurrent ? 'text-primary' : 'text-tertiary'}`}
        >
          {getVersionIcon(group.type)}
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-semibold text-primary'>
              {group.label}
            </span>
            <span className='text-xs text-quaternary'>
              v{lastVersion.version_number} - v{firstVersion.version_number}
            </span>
          </div>
        </div>
        <AccordionChevron className='text-quaternary' />
      </AccordionTrigger>

      <AccordionPanel className='ml-4 mt-1 space-y-0.5 border-l border-secondary pl-3'>
        {group.versions.map(version => {
          const isCurrent = version.id === currentVersionId;
          return (
            <button
              key={version.id}
              onClick={() => onVersionClick?.(version)}
              aria-current={isCurrent ? 'true' : undefined}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors cursor-pointer ${
                isCurrent
                  ? 'bg-secondary ring-1 ring-[var(--color-border-secondary)] ring-inset'
                  : 'hover:bg-secondary'
              }`}
            >
              <span className='text-sm font-semibold text-primary'>
                v{version.version_number}
              </span>
              {isCurrent && (
                <span className='text-xs px-1 py-0.5 bg-tertiary text-secondary rounded font-medium'>
                  Current
                </span>
              )}
              <span className='text-xs text-quaternary ml-auto'>
                {version.created_at
                  ? formatTime(version.created_at, VERSION_TIMESTAMP_OPTIONS)
                  : ''}
              </span>
            </button>
          );
        })}
      </AccordionPanel>
    </AccordionItem>
  );
}
